import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ContractFilters } from '@/shared/types';
import {
  getContracts,
  terminateContract,
  createContract,
  updateContract,
} from '@/backend/services/contracts';

/**
 * Hook to fetch contracts with optional filters
 */
export const useAdminContracts = (filters: ContractFilters = {}) => {
  return useQuery({
    queryKey: ['admin', 'contracts', filters],
    queryFn: () => getContracts(filters),
    placeholderData: keepPreviousData, // Keep previous results while fetching new ones
  });
};

/**
 * Hook to terminate a contract
 */
export const useTerminateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      reason,
    }: {
      id: string;
      reason?: string;
    }) => {
      return terminateContract(id, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'contracts'] });
      queryClient.invalidateQueries({ queryKey: ['my-contract'] });
      // Also invalidate public availability caches so the booking page
      // immediately reflects freed dates after an early termination.
      queryClient.invalidateQueries({ queryKey: ['availableRooms'] });
      queryClient.invalidateQueries({ queryKey: ['roomTypeOccupancy'] });
    },
  });
};

/**
 * Hook to create a new contract
 */
export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createContract,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'contracts'] });
      queryClient.invalidateQueries({ queryKey: ['my-contract'] });
    },
  });
};

/**
 * Hook to update an existing contract
 */
export const useUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: Parameters<typeof updateContract>[1] & { id: string }) => {
      return updateContract(id, data);
    },
    onSuccess: (updatedContract) => {
      // Write the fresh contract directly into the single-contract cache
      // so ContractEdit re-opens with correct values without a round-trip
      queryClient.setQueryData(['contract', updatedContract.id], updatedContract);
      queryClient.invalidateQueries({ queryKey: ['admin', 'contracts'] });
      queryClient.invalidateQueries({ queryKey: ['my-contract'] });
    },
  });
};
